import firebase from 'firebase/app';
import 'firebase/firestore';
import React, { useCallback, useEffect, useState } from 'react';
import { range } from '../utils/range';
import { memoNamed } from '../utils/react/memo-named';
import { AdminAddMemberInfo } from './AdminAddMemberInfo';
import { Checkbox } from './common/checkbox';
import styled from 'styled-components';
import { userInfoTagList, userWithDefault, User, Check } from '../types/user';
import { Root, Explanation, Message, Textarea, H3 } from './common/styled';

function getRandomInt(max: number): number {
  return Math.floor(Math.random() * Math.floor(max));
}

const alp = 'abcdefghijklmnopqrstuvwxyz';
function genTemporaryPassword(length: number = 6): string {
  return range(0, length)
    .map(() => alp[getRandomInt(26)])
    .join('');
}

const SetAdmin = styled.div`
  font-size: 11px;
  text-align: left;
`;

const checkDefault = (input: string): Check => {
  return {
    isCorrect: true,
    alert: `入力「${input}」のチェック時にエラーが発生しました。`,
  };
};

const checkSei =
  userInfoTagList.find((u) => u.tagKey === 'sei')?.check ?? checkDefault;
const checkSeiKana =
  userInfoTagList.find((u) => u.tagKey === 'seiKana')?.check ?? checkDefault;
const checkMei =
  userInfoTagList.find((u) => u.tagKey === 'mei')?.check ?? checkDefault;
const checkMeiKana =
  userInfoTagList.find((u) => u.tagKey === 'meiKana')?.check ?? checkDefault;
const checkEnterYear =
  userInfoTagList.find((u) => u.tagKey === 'enterYear')?.check ?? checkDefault;

export const AdminAddMember = memoNamed('AdminAddMember', () => {
  const [ID, setID] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [output, setOutput] = useState<string>('');
  const [contact, setContact] = useState<boolean>(false);

  const [sei, setSei] = useState<string>('');
  const [mei, setMei] = useState<string>('');
  const [seiKana, setSeiKana] = useState<string>('');
  const [meiKana, setMeiKana] = useState<string>('');
  const [enterYear, setEnterYear] = useState<string>('');
  const [isAdmin, setIsAdmin] = useState<boolean>(false);

  const alertSeiIfEmpty = sei === '' ? '姓は一文字以上入力してください。' : '';
  const alertMeiIfEmpty = mei === '' ? '名は一文字以上入力してください。' : '';
  const alertSei = checkSei(sei).isCorrect ? '' : `姓は${checkSei(sei).alert}`;
  const alertMei = checkMei(mei).isCorrect ? '' : `名は${checkMei(mei).alert}`;
  const alertSeiKana = checkSeiKana(seiKana).isCorrect
    ? ''
    : `せいは${checkSeiKana(seiKana).alert}`;
  const alertMeiKana = checkMeiKana(meiKana).isCorrect
    ? ''
    : `めいは${checkMeiKana(meiKana).alert}`;
  const alertEnterYear =
    checkEnterYear(enterYear).isCorrect || enterYear === ''
      ? ''
      : `入会年度は${checkEnterYear(enterYear).alert}`;

  const check =
    checkSei(sei).isCorrect &&
    checkMei(mei).isCorrect &&
    checkSeiKana(seiKana).isCorrect &&
    checkMeiKana(meiKana).isCorrect &&
    checkEnterYear(enterYear).isCorrect &&
    sei !== '' &&
    mei !== '';

  const alert = `${alertSeiIfEmpty}${alertMeiIfEmpty}${alertSei}${alertMei}${alertSeiKana}${alertMeiKana}${alertEnterYear}`;

  useEffect(() => {
    setOutput(`${ID}${message}`);
  }, [ID, message]);

  const onChangeOutput = useCallback((event) => {
    const _value = event.target.value;
    setOutput(_value);
  }, []);

  const addmember = useCallback(() => {
    // 入力内容チェック
    if (!check) return;
    // 乱数によりIDを生成
    // IDがデータベースに存在しないことを確認
    const _ID = genTemporaryPassword();
    const db = firebase.firestore();
    setContact(true);
    db.collection('Users')
      .where('Identification', '==', _ID)
      .get()
      .then((snapshot) => {
        if (snapshot.size > 0) {
          // if this temporary password is used
          addmember();
          return;
        }

        setID(`仮IDは「 ${_ID} 」です。`);
        // ユーザー情報を取得
        // ユーザー情報をデータベースに登録
        const user: User = userWithDefault({
          sei,
          mei,
          seiKana,
          meiKana,
          enterYear,
          identification: _ID,
          isAdmin,
        });
        db.collection('Users')
          .add(user)
          .then(() => {
            setMessage(
              `${sei} ${mei}様の仮IDを生成しました。トップページ(https://togen-meibo.web.app)の「初めての方」より、"Emailアドレス"と"パスワード"、"仮ID"を入力して新規登録してください。登録後はマイページより情報を確認・編集し、各項目について公開・非公開の設定を行ってください。`
            );
            setContact(false);
            setSei('');
            setMei('');
            setSeiKana('');
            setMeiKana('');
            setEnterYear('');
            setIsAdmin(false);
          })
          .catch((err) => {
            // エラー処理
            console.error(err);
            setMessage('データ保存時にエラーが発生しました。');
            setContact(false);
          });
      })
      .catch((err) => {
        console.error(err);
        setMessage('ID作成時にエラーが発生しました。');
        setContact(false);
      });
  }, [sei, mei, seiKana, meiKana, enterYear, isAdmin, check]);

  return (
    <Root>
      <H3>新規会員情報</H3>
      <Explanation>
        新規に会員を登録します。姓、名、せい、めい、入会年度、管理権限の有無を入力してください。「管理権限を付与する」チェックボックスには、新規会員が、桃源会名簿担当、桃源会会誌担当、桃源会会計担当、Web開発担当のいずれかに該当する者である場合のみ、チェックを入れてください。
      </Explanation>
      <AdminAddMemberInfo
        tagKey='sei'
        tagName='姓'
        value={sei}
        onValueChange={setSei}
      />
      <hr />
      <AdminAddMemberInfo
        tagKey='mei'
        tagName='名'
        value={mei}
        onValueChange={setMei}
      />
      <hr />
      <AdminAddMemberInfo
        tagKey='sei_kana'
        tagName='せい'
        value={seiKana}
        onValueChange={setSeiKana}
      />
      <hr />
      <AdminAddMemberInfo
        tagKey='mei_kana'
        tagName='めい'
        value={meiKana}
        onValueChange={setMeiKana}
      />
      <hr />
      <AdminAddMemberInfo
        tagKey='enter_year'
        tagName='入会年度'
        value={enterYear}
        onValueChange={setEnterYear}
      />
      <hr />
      <SetAdmin>
        <Checkbox checked={isAdmin} onCheckedChange={setIsAdmin} />
        管理者権限を付与する
      </SetAdmin>
      <hr />
      <Message>{alert}</Message>
      <input type='button' value='登録してIDを生成' onClick={addmember} />
      {contact ? ' 通信中...' : ''}
      <br />
      <H3>出力情報</H3>
      <Explanation>
        「登録してIDを生成」ボタンをクリックすると、以下のボックスに新規ユーザ宛のメッセージが表示されます。メッセージには、登録の仮パスワードなどの重要な情報が含まれておりますので、以下のテキストボックスの内容をコピーし、該当新規会員の方に送信してください。このメッセージは保存されません。
      </Explanation>
      <Textarea rows={10} value={output} onChange={onChangeOutput}></Textarea>
      <br />
    </Root>
  );
});
